import { Time } from '@internationalized/date'
import * as React from 'react'
import { DateInput, DateSegment, TimeField, TimeFieldProps, TimeValue } from 'react-aria-components'
import { cn } from '../../utils/cn'

export function TimeInput({
  className,
  defaultValue: defaultValueProp,
  disabled,
  format,
  readOnly,
  size = 'md',
  value: valueProp,
  onValueChange,
  ...props
}: TimeInput.Props) {
  const defaultValue = React.useMemo(() => {
    if (!defaultValueProp) return undefined
    return new Time(...defaultValueProp.split(':').map(Number))
  }, [defaultValueProp])
  const value = React.useMemo(() => {
    if (!valueProp) return undefined
    return new Time(...valueProp.split(':').map(Number))
  }, [valueProp])

  return (
    <TimeField
      className={cn(
        // Base
        'bg-background shadow-xs inset-ring inset-ring-gray-200 flex cursor-default items-center rounded-md',
        // Focus
        'focus-within:inset-ring-accent-600 focus-within:inset-ring-2 focus-within:outline-none',
        // Size
        'data-[size=md]:h-9 data-[size=md]:px-2.5 data-[size=md]:text-sm',
        className
      )}
      data-size={size}
      defaultValue={defaultValue}
      hideTimeZone
      hourCycle={
        format &&
        {
          '12h': 12 as const,
          '24h': 24 as const,
        }[format]
      }
      isDisabled={disabled}
      isReadOnly={readOnly}
      value={value}
      onChange={(value) => {
        if (!value) return
        onValueChange?.(value.toString())
      }}
      {...props}
    >
      <DateInput>
        {(segment) => (
          <DateSegment
            className={cn(
              'rounded-sm p-0.5 data-[type=literal]:p-0 data-[placeholder]:text-gray-400',
              'focus-visible:bg-gray-100 focus-visible:outline-none'
            )}
            segment={segment}
          />
        )}
      </DateInput>
    </TimeField>
  )
}

export namespace TimeInput {
  export interface Props
    extends Pick<
      TimeFieldProps<TimeValue>,
      'autoFocus' | 'className' | 'id' | 'name' | 'onBlur' | 'onFocus' | 'onKeyDown' | 'onKeyUp'
    > {
    defaultValue?: string
    disabled?: boolean
    format?: '12h' | '24h'
    readOnly?: boolean
    size?: 'md'
    value?: string
    onValueChange?: (value: string) => void
  }
}
